import React, { memo } from 'react'
import { cardAttributes } from '../lib/rules'

interface CardFaceProps {
  cardId: number
  className?: string
  decorative?: boolean
}

const COLORS = [
  { name: 'red', ink: '#ea1c2d' },
  { name: 'purple', ink: '#613394' },
  { name: 'green', ink: '#009b4d' }
] as const
const SHAPES = ['squiggle', 'diamond', 'oval'] as const
const SHADINGS = ['solid', 'striped', 'open'] as const
const SHAPE_SPACING = [66, 72, 72] as const

export function describeCard(cardId: number): string {
  const { number, color, shape, shading } = cardAttributes(cardId)
  return `${number + 1} ${SHADINGS[shading]} ${COLORS[color].name} ${SHAPES[shape]}${number === 0 ? '' : 's'}`
}

// Mount once outside the board; hidden/display:none can hide SVG paint servers.
export const CardSymbols = memo(function CardSymbols() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="0" height="0" aria-hidden="true" focusable="false" className="pointer-events-none absolute h-0 w-0 overflow-hidden">
      <defs>
        <path
          id="set-card-squiggle"
          d="M-10-54 C7-54 22.5-41 22.5-19 C22.5-4.5 15 2.5 15 11.5 C15 23.5 25.5 32.5 25.5 39.5 C25.5 46.5 15 50.5 6 50.5 C-13 50.5-23.5 39.5-23.5 22.5 C-23.5 6.5-13.5-6.5-13.5-18 C-13.5-30.5-24.5-40-24.5-45.5 C-24.5-51-17-54-10-54 Z"
        />
        <path id="set-card-diamond" d="M0-59 28 0 0 59-28 0Z" />
        <rect id="set-card-oval" x="-26.5" y="-55" width="53" height="110" rx="26.5" />
        {COLORS.map(({ name, ink }) => (
          <pattern key={name} id={`set-card-stripes-${name}`} width="12" height="12" patternUnits="userSpaceOnUse">
            <path d="M0 3H12" fill="none" stroke={ink} strokeWidth="3.5" />
          </pattern>
        ))}
      </defs>
    </svg>
  )
})

const CardFace = memo(function CardFace({ cardId, className = 'h-auto w-full', decorative = false }: CardFaceProps) {
  const { number, color, shape, shading } = cardAttributes(cardId)
  const { name, ink } = COLORS[color]
  const fill = shading === 0 ? ink : shading === 1 ? `url(#set-card-stripes-${name})` : 'none'

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 258 167"
      width="258"
      height="167"
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : describeCard(cardId)}
      aria-hidden={decorative ? true : undefined}
      focusable="false"
      className={`block aspect-[258/167] bg-white dark:bg-[#f1f0ec] ${className}`}
      fill={fill}
      stroke={ink}
      strokeWidth={shading === 0 ? 0 : shading === 1 ? 4.5 : 6}
      strokeLinejoin="round"
    >
      {Array.from({ length: number + 1 }, (_, index) => (
        <use
          key={index}
          href={`#set-card-${SHAPES[shape]}`}
          transform={`translate(${129 + (index - number / 2) * SHAPE_SPACING[shape]} 83.5)`}
        />
      ))}
    </svg>
  )
})

export default CardFace
